import dayjs from "dayjs";
import useToastContext from "hooks/useToastContext";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import { formatTime12h } from "utils/timeFormat";

export default function TrainingSubmissionDetails() {
  const [submission, setSubmission] = useState(null);
  const { id } = useParams();
  const notification = useToastContext();
  const navigate = useNavigate();

  const fetchSubmission = useCallback(async () => {
    try {
      const response = await apiGet(`${apiPath.trainingSubmissions}/${id}`);
      setSubmission(response?.data?.results || null);
    } catch (error) {
      notification.error(
        error?.response?.data?.message ||
          "Unable to load training submission.",
      );
    }
  }, [id, notification]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const session = submission?.trainingSessionId;

  return (
    <div className="bg-white px-3 py-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Training Submission Details</h1>
        <button
          type="button"
          className="rounded bg-gray-200 px-5 py-2 text-sm"
          onClick={() => navigate("/training-sessions/submissions")}
        >
          Back
        </button>
      </div>
      <hr className="my-4" />

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">Submission Information</h2>
        <div className="grid gap-4 rounded-md border p-4 shadow-lg sm:grid-cols-2 lg:grid-cols-3">
          <Detail label="Agency Name" value={submission?.agencyName} />
          <Detail label="Contact Person" value={submission?.contactPerson} />
          <Detail label="Mobile Number" value={submission?.mobileNumber} />
          <Detail label="Email" value={submission?.email} />
          <Detail
            label="Number of Attendees"
            value={submission?.numberOfAttendees}
          />
          <Detail
            label="Attended"
            value={submission?.isAttended ? "Yes" : "No"}
          />
          <Detail
            label="Created At"
            value={
              submission?.createdAt
                ? dayjs(submission.createdAt).format("DD-MM-YYYY hh:mm A")
                : "N/A"
            }
          />
          <Detail
            label="Last Updated"
            value={
              submission?.updatedAt
                ? dayjs(submission.updatedAt).format("DD-MM-YYYY hh:mm A")
                : "N/A"
            }
          />
        </div>
      </section>

      <hr className="my-6" />

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">Training Session Information</h2>
        <div className="grid gap-4 rounded-md border p-4 shadow-lg sm:grid-cols-2 lg:grid-cols-3">
          <Detail
            label="Date"
            value={session?.date ? dayjs(session.date).format("DD-MM-YYYY") : "N/A"}
          />
          <Detail
            label="Start Time"
            value={formatTime12h(session?.startTime)}
          />
          <Detail label="End Time" value={formatTime12h(session?.endTime)} />
          <Detail label="Location" value={session?.location} />
          <Detail label="Total Seats" value={session?.seats} />
          <Detail
            label="Session Status"
            value={
              session?.status
                ? session.status.charAt(0).toUpperCase() +
                  session.status.slice(1)
                : "N/A"
            }
          />
        </div>
      </section>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-bold">{label}:</p>
      <p>{value ?? "N/A"}</p>
    </div>
  );
}
